<template>
  <div>
    <b-overlay :show="loading" rounded="sm" no-fade>
      <b-card class="mt-2">
        <b-row class="mb-2">
          <table class="table table-sm">
            <thead>
              <tr>
                <th scope="col" class="bg-primary align-middle">
                  Questionnaire Detail - {{ updateQuestionnaire.title }}
                </th>
                <th
                  scope="col"
                  class="bg-primary text-white d-flex justify-content-end align-middle"
                >
                  <b-button
                    v-b-tooltip.hover
                    title="Configure Summary Explanations for different score ranges"
                    variant="secondary"
                    class="animate__animated animate__fadeIn mr-1"
                    style="background-color:white;color:#003b49"
                    size="sm"
                    @click="goToSummaryExplanation(updateQuestionnaire.id)"
                  >
                    Summary Explanation
                  </b-button>

                  <b-button
                    v-b-tooltip.hover
                    title="Manage Files for this Questionnaire"
                    variant="success-dark"
                    class="animate__animated animate__fadeIn mr-1"
                    style="background-color:white;color:#003b49"
                    size="sm"
                    @click="openFilesModal()"
                  >
                    Files
                  </b-button>

                  <b-button
                    v-b-tooltip.hover
                    :title="
                      questions.length == 0
                        ? 'Please add at least one question to the table'
                        : ''
                    "
                    :disabled="questions.length == 0"
                    variant="warning"
                    class="animate__animated animate__fadeIn mr-1"
                    style="background-color:white;color:#003b49"
                    size="sm"
                    @click="
                      copyReview(
                        updateQuestionnaire.id,
                        updateQuestionnaire.title
                      )
                    "
                  >
                    Duplicate
                  </b-button>

                  <b-button
                    v-b-tooltip.hover
                    :title="
                      questions.length == 0
                        ? 'Please add at least one question to the table'
                        : ''
                    "
                    :disabled="questions.length == 0"
                    variant="success"
                    class="animate__animated animate__fadeIn"
                    style="background-color:white;color:#003b49"
                    size="sm"
                    @click="editReview()"
                  >
                    Update Questionnaire
                  </b-button>
                </th>
              </tr>
            </thead>
          </table>
        </b-row>

        <validation-observer ref="questionaireRules2">
          <b-row class="animate__animated ">
            <b-col cols="12" xl="12" md="12" sm="12">
              <!-- 1. Group Management Section -->
              <b-card class="shadow-sm section-item">
                <b-row>
                  <!-- Questionnaire Title -->
                  <b-col cols="12" xl="6" md="6" lg="6" sm="12">
                    <b-form-group
                      label="Questionnaire"
                      label-for="questionnaire"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="questionnaire"
                      >
                        <b-input-group>
                          <b-form-input
                            id="questionnaire"
                            v-model.lazy="updateQuestionnaire.title"
                            type="text"
                            placeholder="Update Questionnaire"
                            :state="questionnaireNameState"
                            aria-describedby="input-live-feedback"
                          ></b-form-input>
                          <small class="text-danger">{{ errors[0] }}</small>
                          <b-form-invalid-feedback id="input-live-feedback">
                            Enter at least 5 letters
                          </b-form-invalid-feedback>
                        </b-input-group>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <!-- Task Selection -->
                  <b-col cols="12" xl="6" md="6" lg="6" sm="12">
                    <b-form-group label="Task (Optional)" label-for="task">
                      <validation-provider #default="{ errors }" name="task">
                        <v-select
                          id="task"
                          v-model="updateQuestionnaire.selectedTask"
                          :options="taskOptions"
                          label="title"
                          :reduce="(task) => task"
                          placeholder="Select a task (optional)"
                          :loading="dropdownLoading"
                          :clearable="true"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <!-- Role Visibility Selections -->
                  <b-col cols="12" xl="6" md="6" lg="6" sm="12">
                    <b-form-group label="Hide Scores From" label-for="edit-hideScoresFrom">
                      <v-select
                        id="edit-hideScoresFrom"
                        v-model="hideScoresFrom"
                        :options="roles"
                        label="name"
                        :reduce="role => role.id"
                        multiple
                        :loading="rolesLoading"
                        placeholder="Select roles"
                        :clearable="true"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" xl="6" md="6" lg="6" sm="12">
                    <b-form-group label="Hide Comments From" label-for="edit-hideCommentsFrom">
                      <v-select
                        id="edit-hideCommentsFrom"
                        v-model="hideCommentsFrom"
                        :options="roles"
                        label="name"
                        :reduce="role => role.id"
                        multiple
                        :loading="rolesLoading"
                        placeholder="Select roles"
                        :clearable="true"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col cols="12" xl="12" md="12" lg="12" sm="12">
                    <b-form-group class="mb-0">
                      <b-form-checkbox
                        id="editableByReviewee"
                        v-model="updateQuestionnaire.editableByReviewee"
                        switch
                      >
                        Editable by reviewee
                      </b-form-checkbox>
                    </b-form-group>
                  </b-col>

                  <!-- Follow-up Config Section -->
                  <b-col cols="12" class="mt-2">
                    <b-card class="border">
                      <h6 class="mb-1">Follow-up Reviews</h6>
                      <b-form-checkbox
                        v-model="followUpConfig.enabled"
                        switch
                        @change="onFollowUpEnabledChange"
                      >
                        Enable follow-up reviews
                      </b-form-checkbox>
                      <div v-if="followUpConfig.enabled" class="mt-1 pl-2">
                        <b-form-checkbox v-model="followUp6Month" @change="syncFollowUpIntervals">
                          6-Month follow-up
                        </b-form-checkbox>
                        <b-form-checkbox v-model="followUp1Year" @change="syncFollowUpIntervals" class="mt-50">
                          1-Year follow-up
                        </b-form-checkbox>
                      </div>
                    </b-card>
                  </b-col>

                  <!-- Groups with Edit/Delete buttons -->
                  <b-col cols="12" xl="6" md="6" sm="12">
                    <b-form-group label="Select Group" label-for="groups">
                      <div class="d-flex align-items-center">
                        <validation-provider
                          #default="{ errors }"
                          name="group"
                          rules="required"
                          class="flex-grow-1"
                        >
                          <v-select
                            id="group"
                            v-model="questionData.group"
                            :taggable="false"
                            :autoscroll="true"
                            maxHeight="50"
                            :create-option="
                              (temp) => ({ id: 0, groupName: temp })
                            "
                            :loading="groupSelectBoxLoading"
                            @option:created="openAddGroupModal"
                            @option:selected="selectedGroupValue"
                            :reduce="(val) => val"
                            :options="groupData"
                            label="groupName"
                            :clearable="true"
                          >
                            <template #option="{ groupName, score, followUpMonths }">
                              <span>{{ groupName
                                }}<span v-if="score !== undefined && score !== null"> (Score: {{ score }})</span
                                ><small v-if="followUpMonths === 6" class="ml-1 text-primary"> — 6-Month Follow-up</small
                                ><small v-else-if="followUpMonths === 12" class="ml-1 text-success"> — 1-Year Follow-up</small
                                ><small v-else class="ml-1 text-muted"> — Initial Review</small
                              ></span>
                            </template>
                            <template #selected-option="{ groupName, score, followUpMonths }">
                              <span>{{ groupName
                                }}<span v-if="score !== undefined && score !== null"> (Score: {{ score }})</span
                                ><small v-if="followUpMonths === 6" class="ml-1 text-primary"> — 6-Month</small
                                ><small v-else-if="followUpMonths === 12" class="ml-1 text-success"> — 1-Year</small
                              ></span>
                            </template>
                            <template
                              #no-options="{ search, searching, loading }"
                            >
                              <div
                                class="d-flex align-items-center justify-content-between p-2"
                              >
                                <span>No options found</span>
                                <b-button
                                  v-if="search"
                                  size="sm"
                                  variant="primary"
                                  @click="openAddGroupModal(search)"
                                >
                                  Add Group Name
                                </b-button>
                              </div>
                            </template>
                          </v-select>
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                        <b-button
                          v-if="questionData.group"
                          size="sm"
                          variant="outline-info"
                          class="ml-1"
                          @click="openEditGroupModal(questionData.group)"
                          v-b-tooltip.hover
                          title="Edit Group"
                        >
                          <feather-icon icon="Edit3Icon" size="16" />
                        </b-button>
                        <b-button
                          v-if="
                            questionData.group &&
                              canDeleteGroup(questionData.group)
                          "
                          size="sm"
                          variant="outline-danger"
                          class="ml-1"
                          @click="deleteGroup(questionData.group)"
                          v-b-tooltip.hover
                          title="Delete Group"
                        >
                          <feather-icon icon="Trash2Icon" size="16" />
                        </b-button>
                        <b-button
                          v-if="
                            questionData.group &&
                              canDeleteGroup(questionData.group)
                          "
                          size="sm"
                          variant="outline-danger"
                          class="ml-1"
                          @click="deleteGroup(questionData.group)"
                          v-b-tooltip.hover
                          title="Delete Group"
                        >
                          <feather-icon icon="Trash2Icon" size="16" />
                        </b-button>
                      </div>
                    </b-form-group>
                  </b-col>

                  <!-- Group Order (drag to reorder) — grouped by interval -->
                  <b-col v-if="groupData.length > 1" cols="12" class="mt-1">
                    <label class="font-weight-bold mb-1">Group Order</label>
                    <small class="text-muted d-block mb-1"
                      >Drag the chips below to reorder groups within each
                      interval, then click
                      <strong>Update Questionnaire</strong> to persist the new
                      order.</small
                    >

                    <!-- Initial Review groups -->
                    <div v-if="initialGroups.length > 0" class="mb-2">
                      <div class="d-flex align-items-center mb-1">
                        <span class="badge badge-pill badge-secondary mr-2">Initial Review</span>
                        <small class="text-muted">{{ initialGroups.length }} group{{ initialGroups.length !== 1 ? 's' : '' }}</small>
                      </div>
                      <draggable
                        v-model="initialGroups"
                        handle=".group-order-handle"
                        :animation="200"
                        ghost-class="group-order-ghost"
                        chosen-class="group-order-chosen"
                        class="group-order-list"
                        tag="div"
                      >
                        <div
                          v-for="(group, index) in initialGroups"
                          :key="group.id"
                          class="group-order-item d-flex align-items-center"
                        >
                          <span class="group-order-handle mr-2">
                            <feather-icon icon="MenuIcon" size="14" class="text-muted" />
                          </span>
                          <span class="group-order-index badge badge-pill badge-light border mr-2">{{ index + 1 }}</span>
                          <span class="font-weight-medium">{{ group.groupName }}</span>
                          <span v-if="group.score !== undefined && group.score !== null" class="ml-2 badge badge-pill badge-info badge-sm">Score: {{ group.score }}</span>
                        </div>
                      </draggable>
                    </div>

                    <!-- 6-Month Follow-up groups -->
                    <div v-if="sixMonthGroups.length > 0" class="mb-2">
                      <div class="d-flex align-items-center mb-1">
                        <span class="badge badge-pill badge-primary mr-2">6-Month Follow-up</span>
                        <small class="text-muted">{{ sixMonthGroups.length }} group{{ sixMonthGroups.length !== 1 ? 's' : '' }}</small>
                      </div>
                      <draggable
                        v-model="sixMonthGroups"
                        handle=".group-order-handle"
                        :animation="200"
                        ghost-class="group-order-ghost"
                        chosen-class="group-order-chosen"
                        class="group-order-list"
                        tag="div"
                      >
                        <div
                          v-for="(group, index) in sixMonthGroups"
                          :key="group.id"
                          class="group-order-item d-flex align-items-center"
                        >
                          <span class="group-order-handle mr-2">
                            <feather-icon icon="MenuIcon" size="14" class="text-muted" />
                          </span>
                          <span class="group-order-index badge badge-pill badge-light border mr-2">{{ index + 1 }}</span>
                          <span class="font-weight-medium">{{ group.groupName }}</span>
                          <span v-if="group.score !== undefined && group.score !== null" class="ml-2 badge badge-pill badge-info badge-sm">Score: {{ group.score }}</span>
                        </div>
                      </draggable>
                    </div>

                    <!-- 1-Year Follow-up groups -->
                    <div v-if="oneYearGroups.length > 0" class="mb-2">
                      <div class="d-flex align-items-center mb-1">
                        <span class="badge badge-pill badge-success mr-2">1-Year Follow-up</span>
                        <small class="text-muted">{{ oneYearGroups.length }} group{{ oneYearGroups.length !== 1 ? 's' : '' }}</small>
                      </div>
                      <draggable
                        v-model="oneYearGroups"
                        handle=".group-order-handle"
                        :animation="200"
                        ghost-class="group-order-ghost"
                        chosen-class="group-order-chosen"
                        class="group-order-list"
                        tag="div"
                      >
                        <div
                          v-for="(group, index) in oneYearGroups"
                          :key="group.id"
                          class="group-order-item d-flex align-items-center"
                        >
                          <span class="group-order-handle mr-2">
                            <feather-icon icon="MenuIcon" size="14" class="text-muted" />
                          </span>
                          <span class="group-order-index badge badge-pill badge-light border mr-2">{{ index + 1 }}</span>
                          <span class="font-weight-medium">{{ group.groupName }}</span>
                          <span v-if="group.score !== undefined && group.score !== null" class="ml-2 badge badge-pill badge-info badge-sm">Score: {{ group.score }}</span>
                        </div>
                      </draggable>
                    </div>
                  </b-col>
                </b-row>
              </b-card>

              <!-- 2. Question Details Section -->
              <b-card class="shadow-sm  mb-1  section-item">
                <b-row>
                  <!-- Question -->
                  <b-col cols="12" md="6" sm="12">
                    <b-form-group label="Question" label-for="Question">
                      <validation-provider
                        #default="{ errors }"
                        name="Question"
                        rules="required|min:5"
                      >
                        <b-form-input
                          id="title"
                          v-model.lazy="questionData.title"
                          type="text"
                          placeholder="Add Question"
                          :state="titleState"
                          aria-describedby="input-live-feedback"
                        ></b-form-input>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <!-- Subtitle Explain - Only for Answer type questions -->
                  <b-col cols="12" md="6" sm="12">
                    <b-form-group
                      label="Subtitle (Optional)"
                      label-for="subtitle"
                    >
                      <b-form-input
                        id="subtitle"
                        v-model="questionData.subtitle"
                        placeholder="Add explanation for this question (optional)"
                        rows="2"
                        no-resize
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row>
                  <!-- Question Type -->
                  <b-col cols="12" sm="12" md="3" lg="3" xl="3">
                    <b-form-group
                      label="Question Type"
                      label-for="questionType"
                    >
                      <validation-provider name="type" #default="{ errors }">
                        <v-select
                          id="type"
                          v-model="questionData.type"
                          :options="questionTypes"
                          :reduce="(val) => val"
                          :filterable="false"
                          label="name"
                          :clearable="false"
                          input-id="QuestionType"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <!-- Max Score -->
                  <b-col
                    cols="12"
                    xl="3"
                    md="3"
                    sm="12"
                    v-if="questionData.type.status === 'A'"
                  >
                    <b-form-group label="Max Score" label-for="maxScore">
                      <validation-provider
                        #default="{ errors }"
                        name="maxScore"
                      >
                        <b-form-input
                          id="maxScore"
                          v-model.number="questionData.maxScore"
                          type="number"
                          min="0"
                          max="100"
                          placeholder="Enter max score (0-100)"
                          :state="maxScoreState"
                        ></b-form-input>
                        <small class="text-danger">{{ errors[0] }}</small>
                        <b-form-invalid-feedback>
                          Max score must be between 0-100 and greater than min
                          score
                        </b-form-invalid-feedback>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <!-- Passing Score -->
                  <b-col
                    cols="12"
                    xl="3"
                    md="3"
                    sm="12"
                    v-if="questionData.type.status === 'A'"
                  >
                    <b-form-group
                      label="Passing Score"
                      label-for="passingScore"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="passingScore"
                      >
                        <b-form-input
                          id="passingScore"
                          v-model.number="questionData.passingScore"
                          type="number"
                          min="0"
                          max="100"
                          placeholder="Enter passing score (0-100)"
                          :state="passingScoreState"
                        ></b-form-input>
                        <small class="text-danger">{{ errors[0] }}</small>
                        <b-form-invalid-feedback>
                          Passing score must be between min and max scores
                        </b-form-invalid-feedback>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <!-- Add Question Button -->
                  <b-col
                    cols="12"
                    xl="3"
                    md="3"
                    sm="12"
                    class="d-flex align-items-end"
                  >
                    <b-button
                      block
                      variant="success"
                      :disabled="questionData.title === '' ? true : false"
                      @click="addQuestion()"
                    >
                      <feather-icon icon="PlusIcon" size="16" class="mr-1" />
                      Add Question
                    </b-button>
                  </b-col>
                </b-row>
              </b-card>

              <!-- 3. Questions List Section -->
              <b-card class="shadow-sm">
                <template #header>
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <h5 class="mb-0 font-weight-bold text-dark">
                      Questions in Review ({{ questions.length }})
                    </h5>
                    <div class="d-flex ml-3 align-items-center">
                      <b-form-input
                        v-model="filter"
                        placeholder="Search questions..."
                        type="search"
                        size="sm"
                        style="width: 200px;"
                      />
                      <small
                        v-if="filter && filter.trim()"
                        class="text-muted ml-2"
                      >
                        (Drag & drop disabled while searching)
                      </small>
                    </div>
                  </div>
                </template>

                <!-- Empty state -->
                <div
                  v-if="filteredQuestions.length === 0 && !filter"
                  class="text-center py-4"
                >
                  <feather-icon
                    icon="AlertCircleIcon"
                    size="48"
                    class="text-muted mb-2"
                  />
                  <h5 class="text-muted">No questions found</h5>
                  <p class="text-muted">
                    Start by adding some questions above.
                  </p>
                </div>

                <!-- No search results -->
                <div
                  v-else-if="filteredQuestions.length === 0 && filter"
                  class="text-center py-4"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="48"
                    class="text-muted mb-2"
                  />
                  <h5 class="text-muted">No results found</h5>
                  <p class="text-muted">Try adjusting your search terms.</p>
                </div>

                <!-- Grouped Questions Table -->
                <div v-else class="table-responsive">
                  <table class="table table-sm table-hover mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th style="width: 40px;" class="text-center">
                          <feather-icon
                            icon="MenuIcon"
                            size="14"
                            class="text-muted"
                          />
                        </th>
                        <th style="width: 25%;">Group</th>
                        <th style="width: 35%;">Question</th>
                        <th style="width: 10%;" class="text-center">Type</th>
                        <th style="width: 10%;" class="text-center">Scores</th>
                        <th style="width: 15%;" class="text-center">Actions</th>
                      </tr>
                    </thead>
                    <!-- Group sections with individual tbody elements -->
                    <tbody
                      v-for="group in groupedQuestions"
                      :key="group.id"
                      class="group-tbody"
                      :data-group-id="group.id"
                    >
                      <!-- Group header row -->
                      <tr class="group-header-row">
                        <td colspan="6" class="group-header">
                          <div
                            class="d-flex justify-content-between align-items-center py-2"
                          >
                            <div class="d-flex align-items-center">
                              <feather-icon
                                icon="FolderIcon"
                                size="16"
                                class="text-info mr-2"
                              />
                              <span class="font-weight-bold text-dark">{{
                                group.groupName
                              }}</span>
                              <span
                                v-if="group.followUpMonths === 6"
                                class="ml-2 badge badge-pill badge-primary badge-sm"
                              >6-Month Follow-up</span>
                              <span
                                v-else-if="group.followUpMonths === 12"
                                class="ml-2 badge badge-pill badge-success badge-sm"
                              >1-Year Follow-up</span>
                              <span
                                v-else
                                class="ml-2 badge badge-pill badge-secondary badge-sm"
                              >Initial Review</span>
                              <span
                                v-if="
                                  group.score !== undefined &&
                                    group.score !== null
                                "
                                class="ml-2 badge badge-pill badge-info badge-sm"
                              >
                                Score: {{ group.score }}
                              </span>
                              <span
                                class="ml-2 badge badge-pill badge-secondary badge-sm"
                              >
                                {{ group.questions.length }} question{{
                                  group.questions.length !== 1 ? "s" : ""
                                }}
                              </span>
                              <span
                                v-if="group.totalMaxScore > 0"
                                class="ml-1 badge badge-pill badge-success badge-sm"
                              >
                                Max: {{ group.totalMaxScore }}
                              </span>
                            </div>
                            <b-button
                              size="sm"
                              variant="outline-info"
                              v-b-tooltip.hover
                              title="Edit Group"
                              class="mr-5"
                              @click="openEditGroupModalFromList(group)"
                            >
                              <feather-icon icon="Edit3Icon" size="14" />
                            </b-button>
                          </div>
                        </td>
                      </tr>

                      <!-- Questions within this group -->
                      <template
                        v-if="group.questions && group.questions.length > 0"
                      >
                        <tr
                          v-for="question in group.questions"
                          :key="question.questionId"
                          :data-question-id="question.questionId"
                          class="question-row"
                          :draggable="!filter || !filter.trim()"
                          @dragstart="
                            onQuestionDragStart($event, question, group)
                          "
                          @dragover.prevent="onQuestionDragOver($event, group)"
                          @drop="onQuestionDrop($event, group)"
                          @dragend="onQuestionDragEnd"
                        >
                          <!-- Drag Handle Column -->
                          <td
                            class="align-middle text-center"
                            style="padding: 8px;"
                          >
                            <div
                              class="drag-handle d-flex justify-content-center align-items-center"
                              style="cursor: grab; width: 24px; height: 24px; margin: 0 auto;"
                            >
                              <feather-icon
                                icon="MenuIcon"
                                size="16"
                                class="text-muted"
                              />
                            </div>
                          </td>

                          <!-- Group Column -->
                          <td class="align-middle">
                            <div class="d-flex align-items-center pl-3">
                              <feather-icon
                                icon="ChevronRightIcon"
                                size="12"
                                class="text-muted mr-1"
                              />
                              <span class="text-muted">{{
                                question.group.groupName
                              }}</span>
                            </div>
                          </td>

                          <!-- Question Column -->
                          <td class="align-middle">
                            <div>
                              <div class="font-weight-medium text-dark">
                                {{ question.title }}
                              </div>
                              <small
                                v-if="question.subtitle"
                                class="text-muted"
                                >{{ question.subtitle }}</small
                              >
                            </div>
                          </td>

                          <!-- Type Column -->
                          <td class="align-middle text-center">
                            <b-badge
                              :variant="
                                question.type.status === 'A'
                                  ? 'success'
                                  : 'warning'
                              "
                              class="badge-sm"
                            >
                              {{
                                question.type.status === "A"
                                  ? "Score"
                                  : "Comment"
                              }}
                            </b-badge>
                          </td>

                          <!-- Scores Column -->
                          <td class="align-middle text-center">
                            <div
                              v-if="question.type.status === 'A'"
                              class="d-flex flex-column"
                            >
                              <small class="text-muted"
                                >Max:
                                <strong>{{ question.maxScore }}</strong></small
                              >
                              <small class="text-muted"
                                >Pass:
                                <strong>{{
                                  question.passingScore
                                }}</strong></small
                              >
                            </div>
                            <span v-else class="text-muted">-</span>
                          </td>

                          <!-- Actions Column -->
                          <td class="align-middle text-center">
                            <div class="btn-group" role="group">
                              <b-button
                                variant="outline-warning"
                                size="sm"
                                v-b-tooltip.hover
                                title="Edit Question"
                                @click="editQuestion(question)"
                              >
                                <feather-icon icon="Edit3Icon" size="14" />
                              </b-button>
                              <b-button
                                variant="outline-danger"
                                size="sm"
                                v-b-tooltip.hover
                                title="Delete Question"
                                @click="deleteQuestion(question.questionId)"
                              >
                                <feather-icon icon="Trash2Icon" size="14" />
                              </b-button>
                            </div>
                          </td>
                        </tr>
                      </template>
                      <!-- Empty state for groups with no questions -->
                      <tr v-else class="empty-group-row">
                        <td colspan="6" class="text-center text-muted py-3">
                          <small>No questions in this group</small>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <!-- Simple Summary Footer -->
                  <div
                    v-if="questions.length > 0"
                    class="mt-3 pt-3 border-top text-center text-muted"
                  >
                    <small>
                      <strong>{{ questions.length }}</strong> question{{
                        questions.length !== 1 ? "s" : ""
                      }}
                      across <strong>{{ groupSummary.length }}</strong> group{{
                        groupSummary.length !== 1 ? "s" : ""
                      }}
                      <span v-if="totalMaxScore > 0">
                        • Total possible score:
                        <strong>{{ totalMaxScore }}</strong> points</span
                      >
                    </small>
                  </div>
                </div>
              </b-card>
            </b-col>
          </b-row>
        </validation-observer>

        <!-- Edit Question Modal -->
        <b-modal
          id="edit-question-modal"
          ref="editQuestionModal"
          v-model="modalShow"
          title="Edit Question"
          @ok="handleOk"
          @hidden="resetModal"
          ok-title="Update"
          cancel-title="Cancel"
          size="lg"
        >
          <validation-observer ref="editQuestionRules">
            <form @submit.stop.prevent="handleOk">
              <b-row>
                <b-col cols="12">
                  <b-form-group label="Question" label-for="editQuestionTitle">
                    <validation-provider
                      name="Question"
                      rules="required|min:5"
                      #default="{ errors }"
                    >
                      <b-form-input
                        id="editQuestionTitle"
                        v-model="editQuestionData.title"
                        placeholder="Enter question"
                        :state="editTitleState"
                      ></b-form-input>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12">
                  <b-form-group
                    label="Subtitle (Optional)"
                    label-for="editQuestionSubtitle"
                  >
                    <b-form-input
                      id="editQuestionSubtitle"
                      v-model="editQuestionData.subtitle"
                      placeholder="Add explanation for this question (optional)"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12" md="4">
                  <b-form-group
                    label="Question Type"
                    label-for="editQuestionType"
                  >
                    <validation-provider name="type" #default="{ errors }">
                      <v-select
                        id="editQuestionType"
                        v-model="editQuestionData.type"
                        :options="questionTypes"
                        :reduce="(val) => val"
                        :filterable="false"
                        label="name"
                        :clearable="false"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  v-if="editQuestionData.type.status === 'A'"
                >
                  <b-form-group label="Max Score" label-for="editMaxScore">
                    <validation-provider #default="{ errors }" name="maxScore">
                      <b-form-input
                        id="editMaxScore"
                        v-model.number="editQuestionData.maxScore"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Enter max score (0-100)"
                        :state="editMaxScoreState"
                      ></b-form-input>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  v-if="editQuestionData.type.status === 'A'"
                >
                  <b-form-group
                    label="Passing Score"
                    label-for="editPassingScore"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="passingScore"
                    >
                      <b-form-input
                        id="editPassingScore"
                        v-model.number="editQuestionData.passingScore"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Enter passing score (0-100)"
                        :state="editPassingScoreState"
                      ></b-form-input>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12">
                  <b-form-group label="Group" label-for="editQuestionGroup">
                    <validation-provider
                      name="group"
                      rules="required"
                      #default="{ errors }"
                    >
                      <v-select
                        id="editQuestionGroup"
                        v-model="editQuestionData.group"
                        :options="groupData"
                        :reduce="(val) => val"
                        label="groupName"
                        :clearable="false"
                      >
                        <template #option="{ groupName, score }">
                          <span
                            >{{ groupName
                            }}<span
                              v-if="score !== undefined && score !== null"
                            >
                              (Score: {{ score }})</span
                            ></span
                          >
                        </template>
                        <template #selected-option="{ groupName, score }">
                          <span
                            >{{ groupName
                            }}<span
                              v-if="score !== undefined && score !== null"
                            >
                              (Score: {{ score }})</span
                            ></span
                          >
                        </template>
                      </v-select>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </form>
          </validation-observer>
        </b-modal>

        <!-- Group Add/Edit Modal -->
        <b-modal
          id="group-modal"
          ref="groupModal"
          v-model="groupModalShow"
          :title="groupModalEditMode ? 'Edit Group' : 'Add Group'"
          @ok="handleGroupModalOk"
          @hidden="resetGroupModal"
          ok-title="Save"
          cancel-title="Cancel"
        >
          <validation-observer ref="groupModalRules">
            <form @submit.stop.prevent="handleGroupModalOk">
              <b-form-group label="Group Name" label-for="groupModalName">
                <validation-provider
                  name="Group Name"
                  rules="required|min:2"
                  #default="{ errors }"
                >
                  <b-form-input
                    id="groupModalName"
                    v-model="groupModalData.groupName"
                    placeholder="Enter group name"
                    :state="
                      groupModalData.groupName &&
                        groupModalData.groupName.length >= 2
                    "
                  ></b-form-input>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                label="Group Score (optional)"
                label-for="groupModalScore"
              >
                <b-form-input
                  id="groupModalScore"
                  v-model.number="groupModalData.score"
                  type="number"
                  min="0"
                  placeholder="Enter group score (optional)"
                ></b-form-input>
              </b-form-group>
              <b-form-group label="Follow-up Stage" label-for="groupFollowUpMonths">
                <v-select
                  id="groupFollowUpMonths"
                  v-model="groupModalData.followUpMonths"
                  :options="followUpStageOptions"
                  :reduce="opt => opt.value"
                  label="label"
                  :clearable="false"
                  placeholder="Select follow-up stage"
                />
                <small class="text-muted">Groups tagged as Initial are shown in all initial reviews. Groups tagged 6-Month or 1-Year appear only in the corresponding follow-up review.</small>
              </b-form-group>
            </form>
          </validation-observer>
        </b-modal>

        <!-- Files Modal -->
        <b-modal
          id="files-modal"
          v-model="showFilesModal"
          title="Questionnaire Files"
          size="lg"
          hide-footer
        >
          <div class="mb-3">
            <b-form-group label="Upload New File" label-for="file-upload">
              <b-form-file
                id="file-upload"
                v-model="selectedFiles"
                placeholder="Choose files or drop them here..."
                drop-placeholder="Drop files here..."
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                multiple
                @change="onFilesSelected"
              ></b-form-file>
            </b-form-group>
            <b-button
              variant="primary"
              :disabled="
                !selectedFiles || selectedFiles.length === 0 || uploadingFiles
              "
              @click="uploadFiles"
            >
              <b-spinner v-if="uploadingFiles" small class="mr-1"></b-spinner>
              {{
                uploadingFiles
                  ? "Uploading..."
                  : `Upload ${
                      selectedFiles && selectedFiles.length > 0
                        ? selectedFiles.length
                        : ""
                    } File${
                      selectedFiles && selectedFiles.length > 1 ? "s" : ""
                    }`
              }}
            </b-button>
          </div>

          <hr />

          <div>
            <h6>Existing Files</h6>
            <div v-if="loadingFiles" class="text-center">
              <b-spinner class="mr-2"></b-spinner>
              Loading files...
            </div>
            <div v-else-if="questionnaireFiles.length === 0" class="text-muted">
              No files uploaded yet.
            </div>
            <b-table
              v-else
              :items="questionnaireFiles"
              :fields="fileTableFields"
              responsive
              small
              striped
              hover
            >
              <template #cell(name)="data">
                <a :href="data.item.url" target="_blank" class="text-primary">
                  {{ data.item.original_name }}
                </a>
              </template>
              <template #cell(size)="data">
                {{ formatFileSize(data.item.size) }}
              </template>
              <template #cell(created_at)="data">
                {{ formatDate(data.item.created_at) }}
              </template>
              <template #cell(actions)="data">
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="deleteFile(data.item.id)"
                >
                  <feather-icon icon="Trash2Icon" size="14"></feather-icon>
                </b-button>
              </template>
            </b-table>
          </div>

          <div class="mt-3 text-right">
            <b-button variant="secondary" @click="showFilesModal = false">
              Close
            </b-button>
          </div>
        </b-modal>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BPagination,
  BFormRating,
  BIcon,
} from "bootstrap-vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";
import settingsStoreModule from "../settingsStoreModule";
import store from "@/store";
import { ref, onUnmounted } from "@vue/composition-api";
import vSelect from "vue-select";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import _ from "lodash";
import router from "@/router";
import axiosIns from "@/libs/axios";
import "animate.css";
import ColourPicker from "vue-colour-picker";
import draggable from "vuedraggable";
import { required, email } from "@validations";
export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BPagination,
    BIcon,
    BFormRating,
    vSelect,
    FeatherIcon,
    //validation
    ValidationProvider,
    ValidationObserver,
    ColourPicker,
    draggable,
  },
  setup() {
    const SETTINGS_APP_STORE_MODULE_NAME = "settings";
    // Register module
    if (!store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
      store.registerModule(SETTINGS_APP_STORE_MODULE_NAME, settingsStoreModule);
    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
        store.unregisterModule(SETTINGS_APP_STORE_MODULE_NAME);
    });
  },
  mounted() {
    this.getQuestionnaireData(this.$route.params.id);
    this.getTasks();
    this.loadRoles();
    // this.getGroups();
  },
  data() {
    return {
      sortBy: "id",
      sortDesc: false,
      dir: "ltr",
      updateQuestionnaire: {
        id: 0,
        title: null,
        selectedTask: null,
        editableByReviewee: false,
      },
      followUpConfig: { enabled: false, intervals: [] },
      followUp6Month: false,
      followUp1Year: false,
      showReviewDetail: false,
      groupSelectBoxLoading: false,
      questionData: {
        title: "",
        subtitle: "",
        type: { name: "Answer", status: "A" },
        group: null,
        maxScore: null,
        passingScore: null,
      },
      editQuestionData: {
        id: null,
        title: "",
        subtitle: "",
        type: { name: "Answer", status: "A" },
        group: null,
        maxScore: null,
        passingScore: null,
      },
      editGroup: {
        id: 0,
        groupName: "",
      },
      groupData: [],
      questionTypes: [
        { name: "Comment", status: "B" },
        { name: "Answer", status: "A" },
      ],
      loading: false,
      edit: null,
      newitem: "",
      preEditQuestionTitle: "",
      preEditQuestionSubtitle: "",
      preEditQuestionMaxScore: null,
      originalTaskId: null, // Orijinal task ID'sini saklamak için
      preEditQuestionPassingScore: null,
      filter: null,
      modalShow: false,
      groupModalShow: false,
      questions: [],
      chartColour: "",
      groupModalEditMode: false,
      groupModalData: {
        groupName: "",
        score: null,
        id: null,
        followUpMonths: null,
      },
      followUpStageOptions: [
        { label: 'Initial Review', value: null },
        { label: '6-Month Follow-up', value: 6 },
        { label: '1-Year Follow-up', value: 12 },
      ],
      draggedQuestion: null,
      draggedFromGroup: null,
      // Task dropdown data
      dropdownLoading: false,
      taskOptions: [],
      // Files Modal Data
      showFilesModal: false,
      selectedFiles: [],
      uploadingFiles: false,
      loadingFiles: false,
      questionnaireFiles: [],
      fileTableFields: [
        { key: "name", label: "File Name", sortable: true },
        { key: "size", label: "Size", sortable: true },
        { key: "created_at", label: "Upload Date", sortable: true },
        { key: "actions", label: "Actions", class: "text-center" },
      ],
      // Roles & visibility
      roles: [],
      rolesLoading: false,
      hideScoresFrom: [],
      hideCommentsFrom: [],
    };
  },
  computed: {
    filteredQuestions: {
      get() {
        // If we're filtering, return read-only filtered results
        if (this.filter && this.filter.trim()) {
          const searchTerm = this.filter.toLowerCase().trim();
          const filtered = this.questions.filter(
            (question) =>
              question.title.toLowerCase().includes(searchTerm) ||
              question.group.groupName.toLowerCase().includes(searchTerm) ||
              (question.subtitle &&
                question.subtitle.toLowerCase().includes(searchTerm))
          );
          // When filtering, sort by group name, then by question id
          return _.orderBy(filtered, ["group.groupName", "id"], ["asc", "asc"]);
        }

        // When not filtering, return questions in their current order for drag and drop
        return this.questions;
      },
      set(value) {
        // Only allow setting when not filtering (during drag operations)
        if (!this.filter || !this.filter.trim()) {
          this.questions = [...value];
        }
      },
    },
    groupSummary() {
      // Create summary for each group
      const grouped = _.groupBy(this.questions, "group.id");

      return Object.keys(grouped)
        .map((groupId) => {
          const questionsInGroup = grouped[groupId];
          const firstQuestion = questionsInGroup[0];

          // Get group data to access score
          const groupData = this.groupData.find((g) => g.id == groupId);

          // Calculate sums for scoring questions only (type A)
          const scoringQuestions = questionsInGroup.filter(
            (q) => q.type.status === "A"
          );
          const totalMaxScore = scoringQuestions.reduce(
            (sum, q) => sum + (q.maxScore || 0),
            0
          );
          const totalPassingScore = scoringQuestions.reduce(
            (sum, q) => sum + (q.passingScore || 0),
            0
          );

          return {
            id: firstQuestion.group.id,
            groupName: firstQuestion.group.groupName,
            score: groupData ? groupData.score : null,
            questionCount: questionsInGroup.length,
            scoringQuestions: scoringQuestions.length,
            totalMaxScore,
            totalPassingScore,
          };
        })
        .sort((a, b) => a.groupName.localeCompare(b.groupName));
    },
    totalMaxScore() {
      return this.questions
        .filter((q) => q.type.status === "A")
        .reduce((sum, q) => sum + (q.maxScore || 0), 0);
    },
    groupedQuestions() {
      let filteredQuestions = this.questions;

      // Apply search filter
      if (this.filter && this.filter.trim()) {
        const searchTerm = this.filter.toLowerCase().trim();
        filteredQuestions = this.questions.filter(
          (question) =>
            question.title.toLowerCase().includes(searchTerm) ||
            question.group.groupName.toLowerCase().includes(searchTerm) ||
            (question.subtitle &&
              question.subtitle.toLowerCase().includes(searchTerm))
        );
      }

      // Group questions by group
      const grouped = _.groupBy(filteredQuestions, "group.id");

      // Use current groupData array order as the canonical group order.
      const groupIndexMap = new Map(
        (this.groupData || []).map((g, idx) => [String(g.id), idx])
      );

      return Object.keys(grouped)
        .map((groupId) => {
          const questionsInGroup = grouped[groupId];
          const firstQuestion = questionsInGroup[0];

          // Get group data to access score
          const groupData = this.groupData.find((g) => g.id == groupId);

          // Calculate sums for scoring questions only (type A)
          const scoringQuestions = questionsInGroup.filter(
            (q) => q.type.status === "A"
          );
          const totalMaxScore = scoringQuestions.reduce(
            (sum, q) => sum + (q.maxScore || 0),
            0
          );
          const totalPassingScore = scoringQuestions.reduce(
            (sum, q) => sum + (q.passingScore || 0),
            0
          );

          return {
            id: firstQuestion.group.id,
            groupName: firstQuestion.group.groupName,
            score: groupData ? groupData.score : null,
            followUpMonths: groupData ? (groupData.followUpMonths ?? null) : null,
            questions: questionsInGroup,
            totalMaxScore,
            totalPassingScore,
            scoringQuestionsCount: scoringQuestions.length,
          };
        })
        .sort((a, b) => {
          const aIdx = groupIndexMap.get(String(a.id));
          const bIdx = groupIndexMap.get(String(b.id));
          if (aIdx === undefined && bIdx === undefined) return a.id - b.id;
          if (aIdx === undefined) return 1;
          if (bIdx === undefined) return -1;
          if (aIdx !== bIdx) return aIdx - bIdx;
          return a.id - b.id;
        });
    },
    // Per-interval group arrays with getter/setter so draggable can reorder within each section
    initialGroups: {
      get() {
        return this.groupData.filter(g => g.followUpMonths == null);
      },
      set(val) {
        const nonInitial = this.groupData.filter(g => g.followUpMonths != null);
        this.groupData = [...val, ...nonInitial];
      },
    },
    sixMonthGroups: {
      get() {
        return this.groupData.filter(g => g.followUpMonths === 6);
      },
      set(val) {
        const initial = this.groupData.filter(g => g.followUpMonths == null);
        const oneYear = this.groupData.filter(g => g.followUpMonths === 12);
        this.groupData = [...initial, ...val, ...oneYear];
      },
    },
    oneYearGroups: {
      get() {
        return this.groupData.filter(g => g.followUpMonths === 12);
      },
      set(val) {
        const initial = this.groupData.filter(g => g.followUpMonths == null);
        const sixMonth = this.groupData.filter(g => g.followUpMonths === 6);
        this.groupData = [...initial, ...sixMonth, ...val];
      },
    },

    questionnaireNameState() {
      if (this.updateQuestionnaire.title === null) {
        return null;
      } else {
        return this.updateQuestionnaire.title.length > 5 ? true : false;
      }
    },
    titleState() {
      if (this.questionData.title === "") {
        return null;
      } else {
        return this.questionData.title.length > 5 ? true : false;
      }
    },
    editTitleState() {
      if (this.editQuestionData.title === "") {
        return null;
      } else {
        return this.editQuestionData.title.length > 5 ? true : false;
      }
    },
    editGroupState() {
      if (this.editGroup.groupName === "") {
        return null;
      } else {
        return this.editGroup.groupName.length > 5 ? true : false;
      }
    },

    editMaxScoreState() {
      if (this.editQuestionData.maxScore === null) return null;
      if (this.editQuestionData.type.status !== "A") return null;

      const score = Number(this.editQuestionData.maxScore);
      if (isNaN(score)) return false;
      if (score < 1 || score > 100) return false;

      return true;
    },
    editPassingScoreState() {
      if (this.editQuestionData.passingScore === null) return null;
      if (this.editQuestionData.type.status !== "A") return null;

      const score = Number(this.editQuestionData.passingScore);
      const maxScore = Number(this.editQuestionData.maxScore);

      if (isNaN(score)) return false;
      if (score < 0 || score > 100) return false;
      if (score > maxScore) return false;

      return true;
    },

    maxScoreState() {
      if (this.questionData.maxScore === null) return null;
      if (this.questionData.type.status !== "A") return null;

      const score = Number(this.questionData.maxScore);
      if (isNaN(score)) return false;
      if (score < 0 || score > 100) return false;

      return true;
    },
    passingScoreState() {
      if (this.questionData.passingScore === null) return null;
      if (this.questionData.type.status !== "A") return null;

      const score = Number(this.questionData.passingScore);
      const maxScore = Number(this.questionData.maxScore);

      if (isNaN(score)) return false;
      if (score < 0 || score > 100) return false;
      if (score > maxScore) return false;

      return true;
    },
  },
  methods: {
    buildGroupOrderPayload() {
      return (this.groupData || [])
        .filter((g) => g && g.id)
        .map((g, idx) => ({ id: g.id, order: idx + 1 }));
    },
    goToSummaryExplanation(surveyId) {
      this.$router.push({
        name: "settings-review-summary-explanation",
        params: { id: surveyId },
      });
    },
    getQuestionnaireData(id) {
      this.loading = true;
      store
        .dispatch("settings/fetchQuestionnaireDetail", id)
        .then((res) => {
          this.updateQuestionnaire.id = res.data.id;
          this.updateQuestionnaire.title = res.data.title;
          this.chartColour = res.data.chartColour;

          // Editable by reviewee (support camelCase + snake_case)
          const rawEditable =
            res.data.editableByReviewee ?? res.data.editable_by_reviewee;
          if (rawEditable === null || rawEditable === undefined) {
            this.updateQuestionnaire.editableByReviewee = false;
          } else if (typeof rawEditable === "boolean") {
            this.updateQuestionnaire.editableByReviewee = rawEditable;
          } else if (typeof rawEditable === "number") {
            this.updateQuestionnaire.editableByReviewee = rawEditable === 1;
          } else if (typeof rawEditable === "string") {
            const v = rawEditable.trim().toLowerCase();
            this.updateQuestionnaire.editableByReviewee =
              v === "true" || v === "1" || v === "yes" || v === "on";
          } else {
            this.updateQuestionnaire.editableByReviewee = Boolean(rawEditable);
          }

          // Load task if available
          if (res.data.task) {
            this.updateQuestionnaire.selectedTask = res.data.task;
            this.originalTaskId = res.data.task.id;
          } else {
            this.originalTaskId = null;
          }

          if (this.$refs.chartColour) {
            this.$refs.chartColour.colorValue = this.chartColour;
          }
          this.showReviewDetail = true;
          this.preQuestionTitle = this.updateQuestionnaire.title;

          // Populate groups and questions from the API response
          this.populateGroupsAndQuestions(res.data.groups);

          // Preload role visibility selections if provided
          this.hideScoresFrom = Array.isArray(res.data.hide_scores_from)
            ? res.data.hide_scores_from.map((r) => (typeof r === 'object' ? r.id : r))
            : [];
          this.hideCommentsFrom = Array.isArray(res.data.hide_comments_from)
            ? res.data.hide_comments_from.map((r) => (typeof r === 'object' ? r.id : r))
            : [];

          // Preload follow-up config
          const fuc = res.data.follow_up_config;
          if (fuc && fuc.enabled) {
            this.followUpConfig = { enabled: true, intervals: fuc.intervals || [] };
            this.followUp6Month = (fuc.intervals || []).some((i) => i.months === 6);
            this.followUp1Year = (fuc.intervals || []).some((i) => i.months === 12);
          } else {
            this.followUpConfig = { enabled: false, intervals: [] };
            this.followUp6Month = false;
            this.followUp1Year = false;
          }

          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          console.log(error);
        });
    },
    populateGroupsAndQuestions(groupsData) {
      // Reset the arrays
      this.groupData = [];
      this.questions = [];

      if (!groupsData || !Array.isArray(groupsData)) {
        return;
      }

      let allQuestions = [];

      groupsData.forEach((group) => {
        // Populate groups array
        this.groupData.push({
          id: group.id,
          groupName: group.groupName,
          status: group.status,
          score: group.score,
          // Prefer backend-provided order if available; otherwise keep array order.
          order:
            group.order ??
            group.group_order ??
            group.groupOrder ??
            group.group_order_index ??
            null,
          followUpMonths: group.follow_up_months ?? null,
        });

        // Populate questions array
        if (group.questions && Array.isArray(group.questions)) {
          group.questions.forEach((question) => {
            allQuestions.push({
              id: question.id,
              questionId: question.id,
              type: { status: question.status },
              group: { id: group.id, groupName: group.groupName },
              title: question.title,
              subtitle: question.subtitle,
              maxScore: question.maxScore,
              passingScore: question.passingScore,
            });
          });
        }
      });

      // If backend sent explicit ordering, honor it.
      if (this.groupData.some((g) => g.order !== null && g.order !== undefined)) {
        this.groupData = [...this.groupData].sort((a, b) => {
          const aOrder = a.order === null || a.order === undefined ? Number.MAX_SAFE_INTEGER : Number(a.order);
          const bOrder = b.order === null || b.order === undefined ? Number.MAX_SAFE_INTEGER : Number(b.order);
          if (aOrder !== bOrder) return aOrder - bOrder;
          return a.id - b.id;
        });
      }

      this.questions = allQuestions;
    },

    getTasks() {
      this.dropdownLoading = true;
      this.$http
        .get("getAllTasks")
        .then((response) => {
          this.taskOptions = response.data;
        })
        .catch((error) => {
          console.error("Error loading tasks:", error);
        })
        .finally(() => {
          this.dropdownLoading = false;
        });
    },

    onRowSelected(items) {},

    onFollowUpEnabledChange(val) {
      if (!val) {
        this.followUp6Month = false;
        this.followUp1Year = false;
        this.followUpConfig.intervals = [];
      }
    },

    syncFollowUpIntervals() {
      const intervals = [];
      if (this.followUp6Month) intervals.push({ label: '6-Month Follow-up', months: 6 });
      if (this.followUp1Year) intervals.push({ label: '1-Year Follow-up', months: 12 });
      this.followUpConfig.intervals = intervals;
    },

    editReview() {
      if (this.updateQuestionnaire.title.length > 5) {
        // Task değişikliği varsa önce review count kontrol et
        if (this.hasTaskChanged()) {
          this.checkReviewCountAndProceed();
        } else {
          this.performUpdate();
        }
      } else {
        this.questionnaireNameState;
      }
    },

    checkReviewCountAndProceed() {
      // Önce bu questionnaire'ı kullanan review sayısını kontrol et
      this.$http
        .get(`checkSurveyCount/${this.updateQuestionnaire.id}`)
        .then((response) => {
          const reviewCount = response.data.reviewCount;
          if (reviewCount > 0) {
            // Review varsa confirmation göster
            this.showTaskChangeConfirmation();
          } else {
            // Review yoksa direkt update et
            this.performUpdate();
          }
        })
        .catch((error) => {
          console.error("Error checking review count:", error);
          // Hata durumunda yine de confirmation göster (güvenlik için)
          this.showTaskChangeConfirmation();
        });
    },

    hasTaskChanged() {
      // Önceki task ile yeni task'ı karşılaştır
      const oldTaskId = this.originalTaskId || null;
      const newTaskId = this.updateQuestionnaire.selectedTask?.id || null;
      return oldTaskId !== newTaskId;
    },

    showTaskChangeConfirmation() {
      this.$swal({
        title: "Are you sure you want to update the task?",
        text:
          "This questionnaire has been used in existing reviews. Changing the task will affect all reviews that use this questionnaire. Do you want to continue?",
        icon: "warning",
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Yes, update all reviews!",
        cancelButtonText: "Cancel",
        denyButtonText: "Create as new questionnaire",
        customClass: {
          confirmButton: "btn btn-success",
          cancelButton: "btn btn-danger",
          denyButton: "btn btn-primary",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          // Tüm anketleri güncelle
          this.performUpdate();
        } else if (result.isDenied) {
          // Yeni questionnaire oluştur
          this.createNewQuestionnaireForTaskChange();
        }
        // Cancel durumunda hiçbir şey yapma
      });
    },

    performUpdate() {
      this.loading = true;
      store
        .dispatch("settings/updateReview", {
          id: this.updateQuestionnaire.id,
          title: this.updateQuestionnaire.title.trim(),
          groups: this.buildGroupOrderPayload(),
          task_id: this.updateQuestionnaire.selectedTask?.id || null,
          hide_scores_from: this.hideScoresFrom,
          hide_comments_from: this.hideCommentsFrom,
          editableByReviewee: this.updateQuestionnaire.editableByReviewee,
          follow_up_config: this.followUpConfig,
          changeTitle:
            this.preQuestionTitle == this.updateQuestionnaire.title.trim()
              ? "no"
              : "yes",
        })
        .then((res) => {
          if (res.status === 200 && res.data.existing == 0) {
            store
              .dispatch("settings/updateQuestionnaireColor", {
                id: this.updateQuestionnaire.id,
                chartColour: this.chartColour,
              })
              .then((res) => {
                this.editToastMessage("Questionnaire");
                this.loading = false;
                router.push({ name: "settings-review-list" });
              })
              .catch((error) => {
                console.log(error);
              });
          } else {
            this.loading = false;
            this.existingToastMessage("Questionnaire");
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    loadRoles() {
      this.rolesLoading = true;
      this.$http
        .get("roles")
        .then((response) => {
          const body = response && response.data ? response.data : [];
          // Support common API shapes: array, {data: [...]}, {roles: [...]}
          const items = Array.isArray(body)
            ? body
            : Array.isArray(body?.data)
            ? body.data
            : Array.isArray(body?.roles)
            ? body.roles
            : [];
          this.roles = items;
        })
        .catch((error) => {
          console.error("Error loading roles:", error);
        })
        .finally(() => {
          this.rolesLoading = false;
        });
    },

    createNewQuestionnaireForTaskChange() {
      const self = this;
      const currentDate = new Date();
      const randomTimeNumbers =
        currentDate.getDay() +
        "" +
        currentDate.getHours() +
        "" +
        currentDate.getMinutes() +
        "" +
        currentDate.getSeconds();

      store
        .dispatch("settings/copyNewQuestionnaireForTaskChange", {
          surveyId: this.updateQuestionnaire.id,
          title:
            this.updateQuestionnaire.title.trim() + " " + randomTimeNumbers,
          task_id: this.updateQuestionnaire.selectedTask?.id || null,
        })
        .then((res) => {
          self.loading = false;
          self
            .$swal(
              "Created!",
              "New questionnaire copy successfully created",
              "success"
            )
            .then((result) => {
              self
                .$swal({
                  title: "Do you want to switch?",
                  text: "Switch to new questionnaire page",
                  icon: "question",
                  showCancelButton: true,
                  confirmButtonText: "Yes, switch!",
                  cancelButtonText: "Stay here",
                  customClass: {
                    confirmButton: "btn btn-success",
                    cancelButton: "btn btn-secondary",
                  },
                })
                .then((switchResult) => {
                  if (switchResult.isConfirmed) {
                    router.push({
                      name: "settings-review-edit",
                      params: { id: res.data.data.id },
                    });
                  }
                });
            });
        })
        .catch((error) => {
          self.loading = false;
          console.log(error);
        });
    },

    copyReview(id, title) {
      const self = this;

      const currentDate = new Date();
      const randomTimeNumbers =
        currentDate.getDay() +
        "" +
        currentDate.getHours() +
        "" +
        currentDate.getMinutes() +
        "" +
        currentDate.getSeconds();

      this.$swal({
        title: "Duplicate Review",
        // text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, duplicate it!",
        input: "text",
        inputPlaceholder: "Enter quality review name",
        inputValue: title + " - copy_" + randomTimeNumbers,
        inputValidator: (value) => {
          if (!value) {
            return "You need to enter a quality review name";
          }
        },

        preConfirm: (val) => {
          return axiosIns
            .post(`copyNameCheck`, { reviewTitle: val })
            .then((res) => {
              if (res.data.existing == 1) {
                throw new Error("This " + title + " name is existing");
              } else {
                return true;
              }
            })
            .catch((error) => {
              this.$swal.showValidationMessage(`${error.message}`);
            });
        },
      }).then((result) => {
        if (result.isConfirmed) {
          const reviewName = document.querySelector(".swal2-input").value;

          store
            .dispatch("settings/copyQuestionnaire", {
              surveyId: id,
              reviewTitle: reviewName,
            })
            .then((res) => {
              let copiedId = res.data.id;
              self
                .$swal({
                  title: "Copied!",
                  text: "New questionnaire duplicate successfully created",
                  icon: "success",
                  showCancelButton: true,
                  confirmButtonColor: "#3085d6",
                  showCancelButton: false,
                })
                .then((result) => {
                  router.push({
                    name: "settings-review-list",
                  });
                });
            })
            .catch((error) => {
              console.log(error);
            });
        }
      });
    },

    addQuestion() {
      const self = this;
      this.$refs.questionaireRules2.validate().then((success) => {
        if (success) {
          const newQuestion = {
            title: this.questionData.title,
            subtitle: this.questionData.subtitle,
            groupId: this.questionData.group.id,
            status: this.questionData.type.status
              ? this.questionData.type.status
              : "A",
            maxScore:
              this.questionData.type.status === "A"
                ? this.questionData.maxScore
                : null,
            passingScore:
              this.questionData.type.status === "A"
                ? this.questionData.passingScore
                : null,
          };
          store
            .dispatch("settings/controlQuestionResultTable", {
              surveyId: this.updateQuestionnaire.id,
              justResultTableControl: 1,
            })
            .then((res) => {
              if (res.data.existing == 1) {
                this.$swal({
                  title: "Are you sure you want to add?",
                  text:
                    "The questionnaire to which we will add this question has been used before. If you say yes, this question will be added to the existing quality review questions.",
                  icon: "warning",
                  showCancelButton: true,
                  showDenyButton: true,
                  confirmButtonText: "Yes, add it!",
                  cancelButtonText: "Cancel",
                  denyButtonText: `Create as new questionnaire`,
                  customClass: {
                    confirmButton: "btn btn-success",
                    cancelButton: "btn btn-danger",
                    denyButton: "btn btn-primary",
                  },
                  // reverseButtons: true,
                }).then((result) => {
                  if (result.isConfirmed) {
                    store
                      .dispatch("settings/addQuestionResultTable", {
                        surveyId: this.updateQuestionnaire.id,
                        addToQuestion: newQuestion,
                      })
                      .then((res) => {
                        self.getGroups();
                        this.clearQuestionData();
                        self.$swal(
                          "Added!",
                          "Your question added successful",
                          "success"
                        );
                      })
                      .catch((error) => {
                        console.log(error);
                      });
                  } else if (result.isDenied) {
                    store
                      .dispatch("settings/copyNewQuestionnaireForAdd", {
                        surveyId: this.updateQuestionnaire.id,
                        newQuestion: newQuestion,
                      })
                      .then((res) => {
                        let copiedId = res.data.id;
                        self
                          .$swal({
                            title: "Copied!",
                            text: "New questionnaire copy successfully created",
                            icon: "success",
                            showCancelButton: true,
                            confirmButtonColor: "#3085d6",
                            cancelButtonColor: "#d33",
                            confirmButtonText:
                              "Switch to new questionnaire page",
                            customClass: {
                              confirmButton: "btn btn-success",
                              cancelButton: "btn btn-danger",
                            },
                          })
                          .then((result) => {
                            if (result.isConfirmed) {
                              router.push({
                                name: "settings-edit-review",
                                params: { id: copiedId },
                                replace: true,
                              });
                              window.location.reload();
                            }
                          });
                      })
                      .catch((error) => {
                        console.log(error);
                      });
                  }
                });
              } else {
                store
                  .dispatch("settings/addNewQuestionItem", newQuestion)
                  .then((res) => {
                    if (res.status === 201) {
                      this.getGroups();
                      this.clearQuestionData();
                      this.toastMessage("New Question");
                    }
                  })
                  .catch((error) => {
                    console.log(error);
                  });
              }
            })
            .catch((error) => {
              console.log(error);
            });
        }
      });
    },

    createNewGroupValue(val) {
      // Redirect to modal for group creation
      this.openAddGroupModal(val.groupName);
    },
    editGroupValue(val) {
      this.groupSelectBoxLoading = true;
      if (
        this.groupData.find(
          (x) => x.groupName.toUpperCase() == val.groupName.toUpperCase()
        ) == undefined
      ) {
        store
          .dispatch("settings/addNewGroupItem", {
            groupName: val.groupName.trim(),
            surveyId: this.updateQuestionnaire.id,
          })
          .then((response) => {
            if (response.status == 201) {
              //this.groupData.unshift(response.data);
              this.questionData.group = response.data;
              this.getGroups();
              this.toastMessage("New Group");
              this.groupSelectBoxLoading = false;
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        this.groupSelectBoxLoading = false;
      }
    },
    editQuestion(question) {
      this.modalShow = true;
      this.preEditQuestionTitle = question.title;
      this.preEditQuestionSubtitle = question.subtitle || "";
      this.preEditQuestionMaxScore = question.maxScore;
      this.preEditQuestionPassingScore = question.passingScore;
      this.editQuestionData = {
        id: question.questionId,
        title: question.title,
        subtitle: question.subtitle,
        group: question.group,
        type:
          question.type.status === "A"
            ? { name: "Answer", status: "A" }
            : { name: "Comment", status: "B" },
        maxScore: question.maxScore,
        passingScore: question.passingScore,
      };
    },
    getGroups() {
      this.groupSelectBoxLoading = true;
      store
        .dispatch("settings/fetchGroups", [])
        .then((res) => {
          const groupsForSurvey = (res.data || []).filter(
            (g) => g && g.surveyId === this.updateQuestionnaire.id
          );

          // Reuse shared normalization logic (order, follow-up months, questions).
          this.populateGroupsAndQuestions(groupsForSurvey);
          this.groupSelectBoxLoading = false;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    selectedGroupValue(val) {
      //   console.log(val);
    },
    deleteQuestion(id) {
      const self = this;
      if (id) {
        store
          .dispatch("settings/controlQuestionResultTable", {
            surveyId: this.updateQuestionnaire.id,
            questionId: id,
            justResultTableControl: 0,
          })
          .then((res) => {
            if (res.data.existing == 1) {
              this.$swal({
                title: "Are you sure you want to delete?",
                text:
                  "There are answered quality review questions related to this question. If you say yes, the quality review questions related to this question will also be deleted.",
                icon: "warning",
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: "Yes, delete it!",
                cancelButtonText: "Cancel",
                denyButtonText: `Create as new questionnaire`,
                customClass: {
                  confirmButton: "btn btn-success",
                  cancelButton: "btn btn-danger",
                  denyButton: "btn btn-primary",
                },
                // reverseButtons: true,
              }).then((result) => {
                if (result.isConfirmed) {
                  store
                    .dispatch("settings/deleteQuestionResultTable", {
                      surveyId: this.updateQuestionnaire.id,
                      questionId: id,
                    })
                    .then((res) => {
                      this.getGroups();
                      this.$swal(
                        "Deleted!",
                        "Your question has been deleted.",
                        "success"
                      );
                    })
                    .catch((error) => {
                      console.log(error);
                    });
                } else if (result.isDenied) {
                  store
                    .dispatch("settings/copyNewQuestionnaireForDelete", {
                      surveyId: this.updateQuestionnaire.id,
                      questionId: id,
                    })
                    .then((res) => {
                      let copiedId = res.data.id;
                      self
                        .$swal({
                          title: "Copied!",
                          text: "New questionnaire copy successfully created",
                          icon: "success",
                          showCancelButton: true,
                          confirmButtonColor: "#3085d6",
                          cancelButtonColor: "#d33",
                          confirmButtonText: "Switch to new questionnaire page",
                          customClass: {
                            confirmButton: "btn btn-success",
                            cancelButton: "btn btn-danger",
                          },
                        })
                        .then((result) => {
                          if (result.isConfirmed) {
                            router.push({
                              name: "settings-edit-review",
                              params: { id: copiedId },
                              replace: true,
                            });
                            window.location.reload();
                          }
                        });
                    })
                    .catch((error) => {
                      console.log(error);
                    });
                }
              });
            } else {
              this.$swal({
                title: "Are you sure you want to delete the question?",
                // text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it!",
              }).then((result) => {
                if (result.isConfirmed) {
                  store
                    .dispatch("settings/deleteQuestion", id)
                    .then((res) => {
                      self.getGroups();
                      self.$swal.fire(
                        "Deleted!",
                        "Question has been deleted.",
                        "success"
                      );
                    })
                    .catch((error) => {
                      console.log(error);
                    });
                }
              });
            }
            //self.$swal.fire('Deleted!', 'Question has been deleted.', 'success');
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
    handleOk(bvModalEvent) {
      bvModalEvent.preventDefault();
      const self = this;
      this.$refs.editQuestionRules.validate().then((success) => {
        if (success) {
          if (this.editQuestionData.title !== "") {
            if (
              this.preEditQuestionTitle !== this.editQuestionData.title ||
              this.preEditQuestionSubtitle !==
                (this.editQuestionData.subtitle || "") ||
              this.preEditQuestionMaxScore !== this.editQuestionData.maxScore ||
              this.preEditQuestionPassingScore !==
                this.editQuestionData.passingScore
            ) {
              store
                .dispatch("settings/controlQuestionResultTable", {
                  surveyId: this.updateQuestionnaire.id,
                  questionId: this.editQuestionData.id,
                  justResultTableControl: 0,
                })
                .then((res) => {
                  if (res.data.existing == 1) {
                    this.$swal({
                      title: "Are you sure you want to update?",
                      text:
                        "There are answered quality review questions related to this question. If you say yes, the quality review questions related to this question will also be updated.",
                      icon: "warning",
                      showCancelButton: true,
                      showDenyButton: true,
                      confirmButtonText: "Yes, update it!",
                      cancelButtonText: "Cancel",
                      denyButtonText: `Create as new questionnaire`,
                      customClass: {
                        confirmButton: "btn btn-success",
                        cancelButton: "btn btn-danger",
                        denyButton: "btn btn-primary",
                      },
                      // reverseButtons: true,
                    }).then((result) => {
                      if (result.isConfirmed) {
                        const editQuestionData = {
                          id: this.editQuestionData.id,
                          title: this.editQuestionData.title,
                          subtitle: this.editQuestionData.subtitle,
                          groupId: this.editQuestionData.group.id,
                          status: this.editQuestionData.type.status,
                          maxScore:
                            this.editQuestionData.type.status === "A"
                              ? this.editQuestionData.maxScore
                              : null,
                          passingScore:
                            this.editQuestionData.type.status === "A"
                              ? this.editQuestionData.passingScore
                              : null,
                        };
                        store
                          .dispatch("settings/updateQuestionResultTable", {
                            surveyId: this.updateQuestionnaire.id,
                            question: editQuestionData,
                          })
                          .then((res) => {
                            self.modalShow = false;
                            self.preEditQuestionTitle = null;
                            self.preEditQuestionSubtitle = "";
                            self.preEditQuestionMaxScore = null;
                            self.preEditQuestionPassingScore = null;
                            self.resetModal();
                            self.getGroups();
                            this.$swal(
                              "Updated!",
                              "Your question has been updated.",
                              "success"
                            );
                          })
                          .catch((error) => {
                            console.log(error);
                          });
                      } else if (result.isDenied) {
                        const editQuestionData = {
                          id: this.editQuestionData.id,
                          title: this.editQuestionData.title,
                          subtitle: this.editQuestionData.subtitle,
                          groupId: this.editQuestionData.group.id,
                          status: this.editQuestionData.type.status,
                          maxScore:
                            this.editQuestionData.type.status === "A"
                              ? this.editQuestionData.maxScore
                              : null,
                          passingScore:
                            this.editQuestionData.type.status === "A"
                              ? this.editQuestionData.passingScore
                              : null,
                        };
                        store
                          .dispatch("settings/copyNewQuestionnaireForUpdate", {
                            surveyId: this.updateQuestionnaire.id,
                            question: editQuestionData,
                          })
                          .then((res) => {
                            self.preEditQuestionTitle = null;
                            self.preEditQuestionSubtitle = "";
                            self.preEditQuestionMaxScore = null;
                            self.preEditQuestionPassingScore = null;
                            self.resetModal();
                            self.modalShow = false;
                            let copiedId = res.data.id;
                            self
                              .$swal({
                                title: "Copied!",
                                text:
                                  "New questionnaire copy successfully created",
                                icon: "success",
                                showCancelButton: true,
                                confirmButtonColor: "#3085d6",
                                cancelButtonColor: "#d33",
                                confirmButtonText:
                                  "Switch to new questionnaire page",
                                customClass: {
                                  confirmButton: "btn btn-success",
                                  cancelButton: "btn btn-danger",
                                },
                              })
                              .then((result) => {
                                if (result.isConfirmed) {
                                  router.push({
                                    name: "settings-edit-review",
                                    params: { id: copiedId },
                                    replace: true,
                                  });
                                  window.location.reload();
                                }
                              });
                          })
                          .catch((error) => {
                            console.log(error);
                          });
                      }
                    });
                  } else {
                    this.handleSubmit();
                    this.preEditQuestionTitle = null;
                    this.preEditQuestionSubtitle = "";
                    this.preEditQuestionMaxScore = null;
                    this.preEditQuestionPassingScore = null;
                  }
                })
                .catch((error) => {
                  console.log(error);
                });
            } else {
              this.handleSubmit();
              this.preEditQuestionTitle = null;
              this.preEditQuestionSubtitle = "";
              this.preEditQuestionMaxScore = null;
              this.preEditQuestionPassingScore = null;
            }
          } else {
          }
        }
      });
    },
    updateGroupName(bvModalEvent) {
      bvModalEvent.preventDefault();
      const self = this;
      if (this.editGroup.groupName.length > 5) {
        store
          .dispatch("settings/controlGroupResultTable", {
            surveyId: this.updateQuestionnaire.id,
            groupId: this.editGroup.id,
          })
          .then((res) => {
            if (res.data.existing == 1) {
              this.$swal({
                title: "Are you sure you want to update?",
                text:
                  "This group name is also used in existing reviews. If you say yes, this group name will be changed in other reviews.",
                icon: "warning",
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: "Yes, update it!",
                cancelButtonText: "Cancel",
                denyButtonText: `Create as new questionnaire`,
                customClass: {
                  confirmButton: "btn btn-success",
                  cancelButton: "btn btn-danger",
                  denyButton: "btn btn-primary",
                },
                // reverseButtons: true,
              }).then((result) => {
                if (result.isConfirmed) {
                  const editGroupData = {
                    id: this.editGroup.id,
                    title: this.editGroup.groupName.trim(),
                  };
                  store
                    .dispatch("settings/updateGroupResultTable", {
                      surveyId: this.updateQuestionnaire.id,
                      group: editGroupData,
                    })
                    .then((res) => {
                      self.modalShow = false;
                      self.resetGroupModal();
                      self.getGroups();
                      this.$swal(
                        "Updated!",
                        "Your group name has been updated.",
                        "success"
                      );
                    })
                    .catch((error) => {
                      console.log(error);
                    });
                } else if (result.isDenied) {
                  const editGroupData = {
                    id: this.editGroup.id,
                    title: this.editGroup.groupName.trim(),
                  };
                  store
                    .dispatch("settings/copyNewQuestionnaireForGroupUpdate", {
                      surveyId: this.updateQuestionnaire.id,
                      group: editGroupData,
                    })
                    .then((res) => {
                      self.modalShow = false;
                      self.resetGroupModal();
                      self.getGroups();
                      let copiedId = res.data.id;
                      self
                        .$swal({
                          title: "Copied!",
                          text: "New group copy successfully created",
                          icon: "success",
                          showCancelButton: true,
                          confirmButtonColor: "#3085d6",
                          cancelButtonColor: "#d33",
                          confirmButtonText: "Switch to new questionnaire page",
                          customClass: {
                            confirmButton: "btn btn-success",
                            cancelButton: "btn btn-danger",
                          },
                        })
                        .then((result) => {
                          if (result.isConfirmed) {
                            router.push({
                              name: "settings-edit-review",
                              params: { id: copiedId },
                              replace: true,
                            });
                            window.location.reload();
                          }
                        });
                    })
                    .catch((error) => {
                      console.log(error);
                    });
                }
              });
            } else {
              store
                .dispatch("settings/editGroup", {
                  id: this.editGroup.id,
                  groupName: this.editGroup.groupName.trim(),
                  score: this.editGroup.score,
                  surveyId: this.updateQuestionnaire.id,
                })
                .then((res) => {
                  this.getGroups();
                  this.resetGroupModal();
                  this.modalShow = false;
                  this.editToastMessage("Group");
                })
                .catch((error) => {
                  console.log(error);
                });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        this.wrongToastMessage("Group");
      }
    },
    handleSubmit() {
      const editQuestionData = {
        id: this.editQuestionData.id,
        title: this.editQuestionData.title,
        subtitle: this.editQuestionData.subtitle,
        groupId: this.editQuestionData.group.id,
        status: this.editQuestionData.type.status,
        maxScore:
          this.editQuestionData.type.status === "A"
            ? this.editQuestionData.maxScore
            : null,
        passingScore:
          this.editQuestionData.type.status === "A"
            ? this.editQuestionData.passingScore
            : null,
      };
      store
        .dispatch("settings/editQuestion", editQuestionData)
        .then((res) => {
          this.getGroups();
          this.resetModal();
          this.groupModalShow = false;
          this.modalShow = false;
          this.editToastMessage("Question");
        })
        .catch((error) => {
          console.log(error);
        });
    },
    resetModal() {
      this.editQuestionData = {
        id: null,
        title: "",
        subtitle: "",
        type: { name: "Answer", status: "A" },
        group: {},
        maxScore: null,
        passingScore: null,
      };
    },
    resetGroupModal() {
      this.groupModalShow = false;
      this.groupModalEditMode = false;
      this.groupModalData = {
        groupName: "",
        score: null,
        id: null,
      };
    },
    openGroupNameModal(group) {
      this.groupModalShow = true;
      this.editGroup = JSON.parse(JSON.stringify(group));
    },
    toastMessage(val) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Successful",
          text: "✔️ " + val + " Added Successful",
          icon: "ThumbsUpIcon",
          variant: "success",
        },
      });
    },
    wrongToastMessage(val) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Warning",
          text: "Please, chek your " + val + " input.",
          icon: "AlertTriangleIcon",
          variant: "warning",
        },
      });
    },
    editToastMessage(val) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Successful",
          text: "✔️ " + val + " Updated Successful",
          icon: "CheckIcon",
          variant: "success",
        },
      });
    },
    existingToastMessage(val) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Warning",
          text: "This " + val + " name is existing",
          icon: "AlertTriangleIcon",
          variant: "warning",
        },
      });
    },
    selectGroupToastMessage(val) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Warning",
          text: "Please select group",
          icon: "AlertTriangleIcon",
          variant: "warning",
        },
      });
    },
    clearQuestionData() {
      this.questionData = {
        title: "",
        subtitle: "",
        type: { name: "Answer", status: "A" },
        group: this.questionData.group, // preserve selected group
        maxScore: null,
        passingScore: null,
      };
    },
    openAddGroupModal(search) {
      this.groupModalData = {
        groupName: search || "",
        score: null,
        id: null,
      };
      this.groupModalEditMode = false;
      this.groupModalShow = true;
    },
    openEditGroupModal(group) {
      const fullGroupData = this.groupData.find((g) => g.id === group.id);
      this.groupModalData = {
        groupName: group.groupName,
        score: group.score,
        id: group.id,
        followUpMonths: fullGroupData ? (fullGroupData.followUpMonths ?? null) : null,
      };
      this.groupModalEditMode = true;
      this.groupModalShow = true;
    },
    openEditGroupModalFromList(group) {
      const fullGroupData = this.groupData.find((g) => g.id === group.id);
      this.groupModalData = {
        groupName: group.groupName,
        score: fullGroupData ? fullGroupData.score : null,
        id: group.id,
        followUpMonths: fullGroupData ? (fullGroupData.followUpMonths ?? group.followUpMonths ?? null) : null,
      };
      this.groupModalEditMode = true;
      this.groupModalShow = true;
    },
    openEditGroupModalFromQuestion(group) {
      const fullGroupData = this.groupData.find((g) => g.id === group.id);
      this.groupModalData = {
        groupName: group.groupName,
        score: fullGroupData ? fullGroupData.score : null,
        id: group.id,
        followUpMonths: fullGroupData ? (fullGroupData.followUpMonths ?? null) : null,
      };
      this.groupModalEditMode = true;
      this.groupModalShow = true;
    },
    getGroupScore(groupId) {
      const groupData = this.groupData.find((g) => g.id === groupId);
      return groupData ? groupData.score : null;
    },
    onQuestionDragStart(evt, question, group) {
      if (this.filter && this.filter.trim()) {
        evt.preventDefault();
        return;
      }

      this.draggedQuestion = question;
      this.draggedFromGroup = group;

      // Set drag effect
      evt.dataTransfer.effectAllowed = "move";
      evt.dataTransfer.setData("text/plain", question.questionId);

      // Add visual feedback
      evt.target.classList.add("dragging");

      // Highlight all questions in the same group as potential drop targets
      const sameGroupRows = document.querySelectorAll(
        `[data-group-id="${group.id}"] .question-row`
      );
      sameGroupRows.forEach((row) => {
        if (!row.classList.contains("dragging")) {
          row.classList.add("potential-drop-zone");
        }
      });
    },

    onQuestionDragOver(evt, targetGroup) {
      if (!this.draggedQuestion || !this.draggedFromGroup) {
        return;
      }

      // Only allow dragover within the same group
      if (this.draggedFromGroup.id !== targetGroup.id) {
        evt.dataTransfer.dropEffect = "none";
        return;
      }

      evt.preventDefault();
      evt.dataTransfer.dropEffect = "move";

      // Simple visual feedback
      const targetRow = evt.target.closest("tr");
      if (
        targetRow &&
        targetRow.classList.contains("question-row") &&
        !targetRow.classList.contains("dragging")
      ) {
        // Remove previous highlights
        document
          .querySelectorAll(".drop-target")
          .forEach((el) => el.classList.remove("drop-target"));
        // Add current highlight
        targetRow.classList.add("drop-target");
      }
    },

    onQuestionDrop(evt, targetGroup) {
      evt.preventDefault();

      if (!this.draggedQuestion || !this.draggedFromGroup) {
        this.resetDragState();
        return;
      }

      // Only allow drops within the same group
      if (this.draggedFromGroup.id !== targetGroup.id) {
        this.resetDragState();
        return;
      }

      const targetRow = evt.target.closest("tr");
      if (!targetRow) {
        this.resetDragState();
        return;
      }

      const targetQuestionId = targetRow.getAttribute("data-question-id");
      if (!targetQuestionId) {
        this.resetDragState();
        return;
      }

      // Store values before resetting state
      const draggedQuestionId = this.draggedQuestion.questionId;
      const fromGroupId = this.draggedFromGroup.id;
      const fromGroupName = this.draggedFromGroup.groupName;

      // Find questions in the source group
      const sourceGroupQuestions = this.questions.filter(
        (q) => q.group.id === fromGroupId
      );
      const draggedIndex = sourceGroupQuestions.findIndex(
        (q) => q.questionId === draggedQuestionId
      );
      const targetIndex = sourceGroupQuestions.findIndex(
        (q) => q.questionId == targetQuestionId
      );

      // Reset drag state first
      this.resetDragState();

      if (
        draggedIndex !== -1 &&
        targetIndex !== -1 &&
        draggedIndex !== targetIndex
      ) {
        // Update the UI immediately
        const questionsClone = [...this.questions];
        const groupQuestions = questionsClone.filter(
          (q) => q.group.id === fromGroupId
        );
        const otherQuestions = questionsClone.filter(
          (q) => q.group.id !== fromGroupId
        );

        // Reorder within the group
        const [removed] = groupQuestions.splice(draggedIndex, 1);
        groupQuestions.splice(targetIndex, 0, removed);

        // Update the main questions array
        this.questions = [...otherQuestions, ...groupQuestions];

        // Show feedback and save to backend
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Reordering...",
            text: "Updating question order",
            icon: "RefreshCwIcon",
            variant: "info",
          },
        });

        // Save the new order
        const groupQuestionOrder = groupQuestions.map((q) => q.questionId);

        this.saveQuestionOrderForGroup(fromGroupId, groupQuestionOrder);
      }
    },

    onQuestionDragEnd() {
      this.resetDragState();
    },

    resetDragState() {
      // Remove all drag-related classes
      document.querySelectorAll(".dragging").forEach((el) => {
        el.classList.remove("dragging");
      });
      document.querySelectorAll(".drop-target").forEach((el) => {
        el.classList.remove("drop-target");
      });
      document.querySelectorAll(".potential-drop-zone").forEach((el) => {
        el.classList.remove("potential-drop-zone");
      });

      this.draggedQuestion = null;
      this.draggedFromGroup = null;
    },

    saveQuestionOrderForGroup(groupId, questionOrder) {
      // Save the question order to the backend
      store
        .dispatch("settings/updateQuestionOrderForGroup", {
          surveyId: this.updateQuestionnaire.id,
          groupId: groupId,
          questionOrder: questionOrder,
        })
        .then((response) => {
          // Success feedback
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Success",
              text: "Question order updated successfully",
              icon: "CheckIcon",
              variant: "success",
            },
          });

          // Optionally refresh the data to ensure consistency
          // this.getGroups();
        })
        .catch((error) => {
          console.error("Error updating question order:", error);

          // Error feedback
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "Failed to update question order. Please try again.",
              icon: "AlertCircleIcon",
              variant: "danger",
            },
          });

          // Revert the UI changes by refreshing the data
          this.getGroups();
        });
    },
    handleGroupModalOk() {
      this.$refs.groupModalRules.validate().then((success) => {
        if (success) {
          if (this.groupModalData.groupName) {
            if (this.groupModalEditMode && this.groupModalData.id) {
              // Edit existing group
              store
                .dispatch("settings/editGroup", {
                  id: this.groupModalData.id,
                  groupName: this.groupModalData.groupName.trim(),
                  score: this.groupModalData.score,
                  follow_up_months: this.groupModalData.followUpMonths,
                  surveyId: this.updateQuestionnaire.id,
                })
                .then((res) => {
                  if (res.status === 200) {
                    this.getGroups();
                    this.editToastMessage("Group");
                    this.groupModalShow = false;
                  }
                })
                .catch((error) => {
                  console.log(error);
                });
            } else {
              // Add new group
              store
                .dispatch("settings/addNewGroupItem", {
                  groupName: this.groupModalData.groupName.trim(),
                  score: this.groupModalData.score,
                  follow_up_months: this.groupModalData.followUpMonths,
                  surveyId: this.updateQuestionnaire.id,
                })
                .then((res) => {
                  if (res.status === 201) {
                    // Automatically select the newly created group
                    this.questionData.group = res.data;
                    this.getGroups();
                    this.toastMessage("New Group");
                    this.groupModalShow = false;
                  }
                })
                .catch((error) => {
                  console.log(error);
                });
            }
          }
        }
      });
    },
    canDeleteGroup(group) {
      // Only allow delete if group has no questions
      const groupQuestions = this.questions.filter(
        (q) => q.group.id === group.id
      );
      return groupQuestions.length === 0;
    },
    deleteGroup(group) {
      const self = this;
      this.$swal({
        title: "Are you sure you want to delete the group?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          store
            .dispatch("settings/deleteGroup", { id: group.id })
            .then((res) => {
              self.getGroups();
              // Clear the selected group if it was the deleted one
              if (
                self.questionData.group &&
                self.questionData.group.id === group.id
              ) {
                self.questionData.group = null;
              }
              self.$swal.fire("Deleted!", "Group has been deleted.", "success");
            })
            .catch((error) => {
              console.log(error);
              self.$swal.fire({
                title: "Error",
                text:
                  "An error occurred while deleting the group. Please try again.",
                icon: "error",
              });
            });
        }
      });
    },
    // Files Modal Methods
    openFilesModal() {
      this.showFilesModal = true;
      this.loadQuestionnaireFiles();
    },
    onFilesSelected(files) {
      this.selectedFiles = files;
    },
    async uploadFiles() {
      if (!this.selectedFiles || this.selectedFiles.length === 0) return;

      this.uploadingFiles = true;
      let successCount = 0;
      let errorCount = 0;

      try {
        // Upload each file one by one
        for (const file of this.selectedFiles) {
          try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("questionnaire_id", this.updateQuestionnaire.id);

            await this.$http.post("questionnaire-files", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });
            successCount++;
          } catch (error) {
            console.error(`Failed to upload ${file.name}:`, error);
            errorCount++;
          }
        }

        // Show combined result
        if (successCount > 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Upload Complete",
              text: `${successCount} file(s) uploaded successfully${
                errorCount > 0 ? `, ${errorCount} failed` : ""
              }`,
              variant: errorCount > 0 ? "warning" : "success",
            },
          });
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Upload Failed",
              text: "All files failed to upload",
              variant: "danger",
            },
          });
        }

        this.selectedFiles = [];
        this.loadQuestionnaireFiles();
      } catch (error) {
        console.error("Upload error:", error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "Upload process failed",
            variant: "danger",
          },
        });
      } finally {
        this.uploadingFiles = false;
      }
    },
    async loadQuestionnaireFiles() {
      this.loadingFiles = true;
      try {
        const response = await this.$http.get(
          `questionnaire-files/${this.updateQuestionnaire.id}`
        );
        this.questionnaireFiles = response.data;
      } catch (error) {
        console.error("Load files error:", error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "Failed to load files",
            variant: "danger",
          },
        });
      } finally {
        this.loadingFiles = false;
      }
    },
    async deleteFile(fileId) {
      const result = await this.$swal.fire({
        title: "Are you sure?",
        text: "This file will be permanently deleted!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        try {
          await this.$http.delete(`questionnaire-files/${fileId}`);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Success",
              text: "File deleted successfully!",
              variant: "success",
            },
          });
          this.loadQuestionnaireFiles();
        } catch (error) {
          console.error("Delete file error:", error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "Failed to delete file",
              variant: "danger",
            },
          });
        }
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
    formatDate(dateString) {
      const date = new Date(dateString);

      // DD/MM/YYYY format
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      // 12-hour format with AM/PM
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";

      hours = hours % 12;
      hours = hours ? hours : 12; // 0 saati 12 yap
      const formattedHours = String(hours).padStart(2, "0");

      return `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;
    },
  },
};
</script>

<style lang="scss">
.swal2-actions {
  display: contents;
  justify-content: center;
}
.swal2-confirm {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.swal2-deny {
  margin-bottom: 0.5rem;
}
.color-input {
  width: 75% !important;
  margin-right: 2px !important;
  height: 37.78px;
  border: 1px solid #d8d6de;
}
.current-color {
  height: 37.78px;
}
.color-picker-container {
  width: 20% !important;
}

/* Color picker palette positioning fix */
.colour-picker {
  position: relative !important;
}

.compact-picker {
  position: absolute !important;
  z-index: 9999 !important;
}
@import "@core/scss/vue/libs/vue-wizard.scss";
@import "@core/scss/vue/libs/vue-select.scss";

/* Compact table styles */
.question-row {
  transition: background-color 0.15s ease;
  position: relative;

  &:hover:not(.sortable-chosen):not(.sortable-ghost) {
    background-color: rgba(46, 183, 224, 0.05) !important;
  }
}

/* Drag and drop styles */
.drag-handle {
  cursor: grab !important;
  user-select: none;
  transition: background-color 0.2s ease;
  border-radius: 4px;

  &:active {
    cursor: grabbing !important;
  }

  &:hover {
    background-color: rgba(0, 123, 255, 0.1) !important;
  }
}

/* Simple drag feedback */
.question-row {
  transition: background-color 0.2s ease;

  &.dragging {
    opacity: 0.5 !important;
    background-color: rgba(0, 123, 255, 0.1) !important;
  }

  &.potential-drop-zone {
    background-color: rgba(0, 123, 255, 0.1) !important;
    border-left: 4px solid rgba(0, 123, 255, 0.5) !important;

    .drag-handle {
      background-color: rgba(0, 123, 255, 0.2) !important;
    }
  }

  &.drop-target {
    background-color: rgba(40, 167, 69, 0.3) !important;
    border: 4px solid #28a745 !important;
    box-shadow: 0 0 15px rgba(40, 167, 69, 0.4) !important;

    &::after {
      content: "↓ DROP HERE ↓";
      position: absolute;
      right: 20px;
      top: 50%;
      transform: translateY(-50%);
      background: #28a745;
      color: white;
      padding: 6px 15px;
      border-radius: 20px;
      font-size: 0.9rem;
      font-weight: bold;
      z-index: 10;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
  }

  &[draggable="false"] {
    .drag-handle {
      cursor: not-allowed !important;
      opacity: 0.3 !important;

      &:hover {
        background-color: transparent !important;
      }
    }
  }
}

.table-sm {
  td {
    vertical-align: middle !important;
    padding: 0.5rem 0.75rem;
    border-top: 1px solid #dee2e6;
  }

  th {
    font-weight: 600;
    font-size: 0.875rem;
    padding: 0.75rem;
    border-bottom: 2px solid #dee2e6;
  }
}

.badge-sm {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.badge-pill.badge-sm {
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
}

.btn-group .btn {
  margin-right: 0 !important;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05) !important;
}

/* Simple summary styling */
.border-top {
  border-top: 1px solid #dee2e6 !important;
}

/* Section styling */
.section-item {
  border-radius: 10px !important;
  padding: 5px !important;
  margin-bottom: 5px !important;
}

/* Empty state styling */
.text-center {
  .feather-icon {
    opacity: 0.5;
  }
}

/* Group header styling */
.group-header-row {
  background-color: #f8f9fa !important;

  .group-header {
    background-color: #f8f9fa !important;
    border-top: 2px solid #e9ecef !important;
    border-bottom: 1px solid #dee2e6 !important;
    padding: 0.1rem !important;

    &:first-child {
      border-top: 2px solid #007bff !important;
    }
  }
}

.group-header-row:hover {
  background-color: #f8f9fa !important;
}

.question-row {
  .pl-3 {
    padding-left: 1rem !important;
  }
}

/* Visual indication that questions belong to a group */
.question-row td:nth-child(2) {
  border-left: 3px solid #f8f9fa;
}

/* Drag and drop visual feedback */
.question-row[draggable="true"] {
  cursor: move;
  transition: all 0.2s ease;
}

.question-row:hover {
  background-color: rgba(0, 123, 255, 0.05) !important;
}

.is-dragging .question-row {
  transition: none !important;
}

.question-row.dragging {
  opacity: 0.5;
  background-color: rgba(0, 123, 255, 0.1) !important;
  transform: scale(0.98);
}

.question-row.drop-target {
  border-top: 3px solid #007bff !important;
  background-color: rgba(0, 123, 255, 0.1) !important;
}

/* Empty group styling */
.empty-group-row {
  background-color: #f8f9fa;
}

/* Group order drag-and-drop styles */
.group-order-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.group-order-item {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.35rem 0.75rem;
  cursor: default;
  user-select: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: #007bff;
    box-shadow: 0 1px 4px rgba(0, 123, 255, 0.2);
  }
}

.group-order-handle {
  cursor: grab;
  display: flex;
  align-items: center;

  &:active {
    cursor: grabbing;
  }
}

.group-order-index {
  font-size: 0.7rem;
  min-width: 1.4rem;
  text-align: center;
}

.group-order-ghost {
  opacity: 0.4 !important;
  background: rgba(0, 123, 255, 0.08) !important;
  border: 1px dashed #007bff !important;
}

.group-order-chosen {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border-color: #007bff !important;
  background: #fff !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-responsive {
    .table td,
    .table th {
      padding: 0.25rem 0.5rem;
      font-size: 0.875rem;
    }

    .btn-group .btn {
      padding: 0.25rem 0.5rem;
    }

    .badge-sm {
      font-size: 0.7rem;
      padding: 0.2rem 0.3rem;
    }

    .group-header {
      padding: 0.5rem !important;

      .d-flex {
        flex-direction: column;
        align-items: flex-start !important;

        > div:first-child {
          margin-bottom: 0.5rem;
        }
      }
    }
  }
}
</style>
